import { useTranslation } from 'react-i18next';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import { SupportedIsraeliIdChangeRequestDtoIn } from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import styles from './IsraeliIdChange.module.css';

interface IsraeliIdChangeDotProps {
  request: SupportedIsraeliIdChangeRequestDtoIn;
  /** What the bubble says. The coordinator's wording by default. */
  label?: string;
  /** Makes the dot a button - the office opens the request from it. */
  onClick?: () => void;
}

/**
 * Beside a supported's name in the list (HAT-1428): a dot, with what it means in a bubble on hover -
 * an ID correction waiting for the office, or a decision the coordinator has not seen yet. A dot and
 * not a label under the name: the rows have a fixed height, and a second line spilled into the next
 * row (Chaya, 20.10). The banner above the board is what makes sure it is noticed.
 */
const IsraeliIdChangeDot = ({ request, label, onClick }: IsraeliIdChangeDotProps) => {
  const { t } = useTranslation();
  const id = `id-change-dot-${request.contactId}`;
  const colour = request.status === 'Approved' ? styles.dotApproved : request.status === 'Rejected' ? styles.dotRejected : styles.dotPending;
  const text = label ?? t(`israeliIdChangeRequest.dot.${request.status}`, { id: request.requestedIsraeliId });

  return (
    <>
      {onClick ? (
        <button
          type="button"
          id={id}
          className={`${styles.dot} ${styles.dotButton} ${colour}`}
          aria-label={text}
          onClick={(e) => {
            // The row's own click opens the blade
            e.stopPropagation();
            onClick();
          }}
        />
      ) : (
        <span id={id} className={`${styles.dot} ${colour}`} role="img" aria-label={text} />
      )}
      <CustomTooltip targetId={id}>
        <p className="body-xs-sm-regular">{text}</p>
      </CustomTooltip>
    </>
  );
};

export default IsraeliIdChangeDot;
